import React, { useState, useEffect } from 'react';
import {
  FileText,
  Download,
  Calendar,
  Filter,
  BarChart3,
  TrendingUp,
  DollarSign,
  MapPin,
  Clock,
  AlertCircle,
  RefreshCw,
  Settings,
  Eye,
  Users,
  Building,
  CheckCircle,
  XCircle
} from 'lucide-react';
import { useWorks } from '../../contexts/WorksContext';
import { WorkFilters, WorkStage, GrantLevel, WorkType } from '../../types/work.types';
import { worksApiService } from '../../services/workApi.service';

// ==========================================
// TYPES
// ==========================================

interface ReportConfig {
  title: string;
  description: string;
  icon: React.ComponentType<{ className?: string }>;
  color: string;
  bgColor: string;
  category: 'financial' | 'progress' | 'operational' | 'compliance';
  filters?: Partial<WorkFilters>;
}

interface ReportCardProps extends ReportConfig {
  onGenerate: () => void;
  loading?: boolean;
}

// ==========================================
// REPORT CARD COMPONENT
// ==========================================

const ReportCard: React.FC<ReportCardProps> = ({
  title,
  description,
  icon: Icon,
  color,
  bgColor,
  onGenerate,
  loading = false
}) => {
  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 hover:shadow-md transition-all duration-200 group">
      <div className="flex items-start space-x-4">
        <div className={`p-3 rounded-lg ${bgColor} group-hover:scale-110 transition-transform duration-200`}>
          <Icon className={`w-6 h-6 ${color}`} />
        </div>
        <div className="flex-1">
          <h3 className="text-lg font-semibold text-gray-900 mb-2">{title}</h3>
          <p className="text-gray-600 text-sm mb-4 leading-relaxed">{description}</p>
          <button
            onClick={onGenerate}
            disabled={loading}
            className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? (
              <RefreshCw className="w-4 h-4 animate-spin" />
            ) : (
              <Download className="w-4 h-4" />
            )}
            <span>{loading ? 'Generating...' : 'Generate Report'}</span>
          </button>
        </div>
      </div>
    </div>
  );
};

// ==========================================
// FILTER PANEL COMPONENT
// ==========================================

const ReportFiltersPanel: React.FC<{
  filters: WorkFilters;
  onFiltersChange: (filters: WorkFilters) => void;
  onReset: () => void;
}> = ({ filters, onFiltersChange, onReset }) => {
  const [isExpanded, setIsExpanded] = useState(false);

  const handleFilterChange = (key: keyof WorkFilters, value: any) => {
    onFiltersChange({ ...filters, [key]: value });
  };

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-8">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-gray-900 flex items-center">
          <Filter className="w-5 h-5 mr-2 text-blue-600" />
          Report Filters
        </h3>
        <div className="flex space-x-2">
          <button
            onClick={() => setIsExpanded(!isExpanded)}
            className="flex items-center space-x-2 px-3 py-1 text-sm text-gray-600 hover:text-gray-900 transition-colors"
          >
            <Settings className="w-4 h-4" />
            <span>{isExpanded ? 'Hide' : 'Show'} Advanced</span>
          </button>
          <button
            onClick={onReset}
            className="px-3 py-1 text-sm text-red-600 hover:text-red-800 transition-colors"
          >
            Reset
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-4">
        {/* Date Range */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">From Date</label>
          <input
            type="date"
            value={filters.fromDate ? filters.fromDate.toISOString().split('T')[0] : ''}
            onChange={(e) => handleFilterChange('fromDate', e.target.value ? new Date(e.target.value) : undefined)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">To Date</label>
          <input
            type="date"
            value={filters.toDate ? filters.toDate.toISOString().split('T')[0] : ''}
            onChange={(e) => handleFilterChange('toDate', e.target.value ? new Date(e.target.value) : undefined)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          />
        </div>

        {/* Grant Level */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Grant Level</label>
          <select
            value={filters.grantLevel || ''}
            onChange={(e) => handleFilterChange('grantLevel', e.target.value || undefined)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          >
            <option value="">All Grant Levels</option>
            {Object.values(GrantLevel).map(level => (
              <option key={level} value={level}>{level}</option>
            ))}
          </select>
        </div>

        {/* Work Stage */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Work Stage</label>
          <select
            value={filters.stage || ''}
            onChange={(e) => handleFilterChange('stage', e.target.value || undefined)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          >
            <option value="">All Stages</option>
            {Object.values(WorkStage).map(stage => (
              <option key={stage} value={stage}>{stage}</option>
            ))}
          </select>
        </div>
      </div>

      {isExpanded && (
        <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-4 mt-4 pt-4 border-t border-gray-200">
          {/* Cost Range */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Min Cost</label>
            <input
              type="number"
              value={filters.minCost || ''}
              onChange={(e) => handleFilterChange('minCost', e.target.value ? Number(e.target.value) : undefined)}
              placeholder="₹ 0"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Max Cost</label>
            <input
              type="number"
              value={filters.maxCost || ''}
              onChange={(e) => handleFilterChange('maxCost', e.target.value ? Number(e.target.value) : undefined)}
              placeholder="₹ ∞"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          {/* Search Term */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Search Term</label>
            <input
              type="text"
              value={filters.searchTerm || ''}
              onChange={(e) => handleFilterChange('searchTerm', e.target.value || undefined)}
              placeholder="Work name, agency..."
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          {/* Agency Name */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Agency Name</label>
            <input
              type="text"
              value={filters.agencyName || ''}
              onChange={(e) => handleFilterChange('agencyName', e.target.value || undefined)}
              placeholder="Agency name"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
        </div>
      )}

      {/* Active Filters Summary */}
      {Object.keys(filters).length > 0 && (
        <div className="mt-4 pt-4 border-t border-gray-200">
          <p className="text-sm text-gray-600">
            <span className="font-medium">Active Filters:</span> {Object.keys(filters).length} filter(s) applied
          </p>
        </div>
      )}
    </div>
  );
};

// ==========================================
// MAIN REPORTS COMPONENT
// ==========================================

const WorksReports: React.FC = () => {
  const { state, fetchWorks } = useWorks();
  const [loading, setLoading] = useState<Record<string, boolean>>({});
  const [error, setError] = useState<string | null>(null);
  const [reportFilters, setReportFilters] = useState<WorkFilters>({});
  const [selectedCategory, setSelectedCategory] = useState<string>('all');

  // ==========================================
  // REPORT CONFIGURATIONS
  // ==========================================

  const reportConfigs: Record<string, ReportConfig> = {
    progress: {
      title: 'Progress Summary Report',
      description: 'Comprehensive overview of work progress by district, stage, and completion status.',
      icon: BarChart3,
      color: 'text-blue-600',
      bgColor: 'bg-blue-100',
      category: 'progress'
    },
    financial: {
      title: 'Financial Analysis Report',
      description: 'Detailed financial breakdown including expenditure, utilization rates, and pending bills.',
      icon: DollarSign,
      color: 'text-green-600',
      bgColor: 'bg-green-100',
      category: 'financial'
    },
    delayed: {
      title: 'Delayed Works Report',
      description: 'List of all works that are past their completion dates with detailed analysis.',
      icon: AlertCircle,
      color: 'text-red-600',
      bgColor: 'bg-red-100',
      category: 'operational',
      filters: { stage: WorkStage.IN_PROGRESS }
    },
    district: {
      title: 'District-wise Performance',
      description: 'Performance comparison across all districts with key metrics and rankings.',
      icon: MapPin,
      color: 'text-purple-600',
      bgColor: 'bg-purple-100',
      category: 'progress'
    },
    contractor: {
      title: 'Contractor Performance Report',
      description: 'Analysis of contractor performance, completion rates, and work quality metrics.',
      icon: Users,
      color: 'text-indigo-600',
      bgColor: 'bg-indigo-100',
      category: 'operational'
    },
    monthly: {
      title: 'Monthly Trends Report',
      description: 'Month-wise analysis of work sanctions, expenditure, and completion trends.',
      icon: TrendingUp,
      color: 'text-orange-600',
      bgColor: 'bg-orange-100',
      category: 'progress'
    },
    compliance: {
      title: 'Compliance Status Report',
      description: 'Regulatory compliance status, pending approvals, and documentation completeness.',
      icon: CheckCircle,
      color: 'text-teal-600',
      bgColor: 'bg-teal-100',
      category: 'compliance'
    },
    executive: {
      title: 'Executive Summary',
      description: 'High-level executive dashboard with key performance indicators and insights.',
      icon: FileText,
      color: 'text-gray-600',
      bgColor: 'bg-gray-100',
      category: 'progress'
    }
  };

  // ==========================================
  // UTILITY FUNCTIONS
  // ==========================================

  const downloadReport = (data: any, filename: string, type: 'json' | 'csv' = 'json') => {
    let content: string;
    let mimeType: string;

    if (type === 'csv') {
      // Convert to CSV
      const headers = Object.keys(data[0] || {});
      const csvContent = [
        headers.join(','),
        ...data.map((row: any) => headers.map(header => JSON.stringify(row[header] || '')).join(','))
      ].join('\n');
      content = csvContent;
      mimeType = 'text/csv';
    } else {
      content = JSON.stringify(data, null, 2);
      mimeType = 'application/json';
    }

    const blob = new Blob([content], { type: mimeType });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const setReportLoading = (reportKey: string, isLoading: boolean) => {
    setLoading(prev => ({ ...prev, [reportKey]: isLoading }));
  };

  // ==========================================
  // REPORT GENERATORS
  // ==========================================

  const generateProgressReport = async () => {
    try {
      setReportLoading('progress', true);
      setError(null);
      
      const report = await worksApiService.generateProgressReport(reportFilters.districtId);
      
      const reportData = {
        title: 'Works Progress Report',
        generatedAt: new Date().toISOString(),
        filters: reportFilters,
        summary: report.summary,
        districtProgress: report.districtProgress,
        stageProgress: report.stageProgress,
        delayedWorks: report.delayedWorks,
        completionForecast: report.completionForecast,
        insights: {
          totalWorks: report.summary.total_works,
          completionRate: ((report.summary.total_works - report.delayedWorks.length) / report.summary.total_works * 100).toFixed(1),
          averageProgress: report.summary.physical_progress_percentage.toFixed(1),
          topPerformingDistrict: report.districtProgress.reduce((top, district) => 
            district.financial_progress_percentage > (top?.financial_progress_percentage || 0) ? district : top, null as any)?.district_name
        }
      };
      
      downloadReport(reportData, `works_progress_report_${new Date().toISOString().split('T')[0]}.json`);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to generate progress report');
    } finally {
      setReportLoading('progress', false);
    }
  };

  const generateFinancialReport = async () => {
    try {
      setReportLoading('financial', true);
      setError(null);
      
      const report = await worksApiService.generateFinancialReport();
      
      const reportData = {
        title: 'Works Financial Report',
        generatedAt: new Date().toISOString(),
        overview: report.overview,
        grantAnalysis: report.grantAnalysis,
        pendingBills: report.pendingBills,
        expenditureTrends: report.expenditureTrends,
        insights: {
          utilizationRate: report.overview.utilization_percentage.toFixed(1),
          totalPendingAmount: report.pendingBills.reduce((sum, work) => sum , 0),
          topGrantType: report.grantAnalysis.reduce((top, grant) => 
            grant.total_sanctioned > (top?.total_sanctioned || 0) ? grant : top, null as any)?.grant_type,
          monthlyAverage: report.expenditureTrends.reduce((sum, trend) => sum + trend.amount_spent, 0) / report.expenditureTrends.length
        }
      };
      
      downloadReport(reportData, `works_financial_report_${new Date().toISOString().split('T')[0]}.json`);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to generate financial report');
    } finally {
      setReportLoading('financial', false);
    }
  };

  const generateDelayedWorksReport = async () => {
    try {
      setReportLoading('delayed', true);
      setError(null);
      
      const delayedWorks = await worksApiService.getDelayedWorks();
      
      const reportData = {
        title: 'Delayed Works Report',
        generatedAt: new Date().toISOString(),
        totalDelayed: delayedWorks.length,
        works: delayedWorks.map(work => ({
          sno: work.sno,
          work_name: work.work_name,
          district: work.district?.name,
          mandal: work.mandal?.name,
          agency: work.agency_name,
          completion_date_set: work.completion_date_set,
          days_overdue: work.completion_date_set ? 
            Math.floor((new Date().getTime() - new Date(work.completion_date_set).getTime()) / (1000 * 60 * 60 * 24)) : 0,
          estimate_cost: work.estimate_cost,
          cum_exp_on_work: work.cum_exp_on_work,
          stage: work.abstract_stage_of_work,
          financial_progress: ((work.cum_exp_on_work / (work.ras_amount || work.estimate_cost)) * 100).toFixed(1)
        })),
        summary: {
          totalValue: delayedWorks.reduce((sum, work) => sum + work.estimate_cost, 0),
          totalSpent: delayedWorks.reduce((sum, work) => sum + work.cum_exp_on_work, 0),
          averageDelay: delayedWorks.reduce((sum, work) => {
            const days = work.completion_date_set ? 
              Math.floor((new Date().getTime() - new Date(work.completion_date_set).getTime()) / (1000 * 60 * 60 * 24)) : 0;
            return sum + days;
          }, 0) / delayedWorks.length,
          criticalWorks: delayedWorks.filter(work => {
            const days = work.completion_date_set ? 
              Math.floor((new Date().getTime() - new Date(work.completion_date_set).getTime()) / (1000 * 60 * 60 * 24)) : 0;
            return days > 90;
          }).length
        }
      };
      
      downloadReport(reportData, `delayed_works_report_${new Date().toISOString().split('T')[0]}.json`);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to generate delayed works report');
    } finally {
      setReportLoading('delayed', false);
    }
  };

  const generateDistrictReport = async () => {
    try {
      setReportLoading('district', true);
      setError(null);
      
      const districtSummary = await worksApiService.getDistrictWiseSummary();
      
      const reportData = {
        title: 'District-wise Performance Report',
        generatedAt: new Date().toISOString(),
        districts: districtSummary.map((district, index) => ({
          ...district,
          rank: index + 1,
          efficiency_score: (district.financial_progress_percentage + district.physical_progress_percentage) / 2,
          cost_per_work: district.total_estimate_cost / district.total_works,
          completion_rate: (district.total_works - (district.total_works * 0.1)) / district.total_works * 100 // Placeholder calculation
        })),
        insights: {
          topPerformer: districtSummary[0]?.district_name,
          totalDistricts: districtSummary.length,
          averageWorks: districtSummary.reduce((sum, d) => sum + d.total_works, 0) / districtSummary.length,
          averageProgress: districtSummary.reduce((sum, d) => sum + d.financial_progress_percentage, 0) / districtSummary.length
        }
      };
      
      downloadReport(reportData, `district_performance_report_${new Date().toISOString().split('T')[0]}.json`);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to generate district report');
    } finally {
      setReportLoading('district', false);
    }
  };

  const generateContractorReport = async () => {
    try {
      setReportLoading('contractor', true);
      setError(null);
      
      const performance = await worksApiService.getPerformanceAnalytics();
      
      const reportData = {
        title: 'Contractor Performance Report',
        generatedAt: new Date().toISOString(),
        contractors: performance.contractorPerformance,
        insights: {
          totalContractors: performance.contractorPerformance.length,
          averagePerformance: performance.contractorPerformance.reduce((sum, c) => sum + c.performance_score, 0) / performance.contractorPerformance.length,
          topContractor: performance.contractorPerformance.reduce((top, contractor) => 
            contractor.performance_score > (top?.performance_score || 0) ? contractor : top, null as any)?.contractor_name,
          totalValue: performance.contractorPerformance.reduce((sum, c) => sum + c.total_contract_value, 0)
        }
      };
      
      downloadReport(reportData, `contractor_performance_report_${new Date().toISOString().split('T')[0]}.json`);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to generate contractor report');
    } finally {
      setReportLoading('contractor', false);
    }
  };

  const generateMonthlyReport = async () => {
    try {
      setReportLoading('monthly', true);
      setError(null);
      
      const dashboard = await worksApiService.getFinancialDashboard();
      
      const reportData = {
        title: 'Monthly Trends Report',
        generatedAt: new Date().toISOString(),
        trends: dashboard.trendAnalysis,
        insights: {
          totalMonths: dashboard.trendAnalysis.length,
          averageMonthlySpent: dashboard.trendAnalysis.reduce((sum, t) => sum + t.amount_spent, 0) / dashboard.trendAnalysis.length,
          peakMonth: dashboard.trendAnalysis.reduce((peak, trend) => 
            trend.amount_spent > (peak?.amount_spent || 0) ? trend : peak, null as any)?.month,
          growthRate: dashboard.trendAnalysis.length > 1 ? 
            ((dashboard.trendAnalysis[dashboard.trendAnalysis.length - 1].amount_spent - dashboard.trendAnalysis[0].amount_spent) / dashboard.trendAnalysis[0].amount_spent * 100) : 0
        }
      };
      
      downloadReport(reportData, `monthly_trends_report_${new Date().toISOString().split('T')[0]}.json`);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to generate monthly report');
    } finally {
      setReportLoading('monthly', false);
    }
  };

  const generateComplianceReport = async () => {
    try {
      setReportLoading('compliance', true);
      setError(null);
      
      // Simulate compliance data based on works
      const works = state.works;
      const complianceData = works.map(work => ({
        sno: work.sno,
        work_name: work.work_name,
        has_oas_proceedings: !!work.oas_proceedings_number,
        has_ras_proceedings: !!work.ras_proceedings_number,
        has_agreement: !!work.agreement_date,
        has_completion_date: !!work.completion_date_set,
        documentation_score: [
          !!work.oas_proceedings_number,
          !!work.ras_proceedings_number,
          !!work.agreement_date,
          !!work.completion_date_set
        ].filter(Boolean).length * 25,
        compliance_status: [
          !!work.oas_proceedings_number,
          !!work.ras_proceedings_number,
          !!work.agreement_date,
          !!work.completion_date_set
        ].filter(Boolean).length >= 3 ? 'Compliant' : 'Non-Compliant'
      }));
      
      const reportData = {
        title: 'Compliance Status Report',
        generatedAt: new Date().toISOString(),
        works: complianceData,
        summary: {
          totalWorks: complianceData.length,
          compliantWorks: complianceData.filter(w => w.compliance_status === 'Compliant').length,
          nonCompliantWorks: complianceData.filter(w => w.compliance_status === 'Non-Compliant').length,
          averageDocumentationScore: complianceData.reduce((sum, w) => sum + w.documentation_score, 0) / complianceData.length,
          complianceRate: (complianceData.filter(w => w.compliance_status === 'Compliant').length / complianceData.length * 100).toFixed(1)
        }
      };
      
      downloadReport(reportData, `compliance_report_${new Date().toISOString().split('T')[0]}.json`);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to generate compliance report');
    } finally {
      setReportLoading('compliance', false);
    }
  };

  const generateExecutiveReport = async () => {
    try {
      setReportLoading('executive', true);
      setError(null);
      
      const [dashboard, progress, performance] = await Promise.all([
        worksApiService.getFinancialDashboard(),
        worksApiService.getProgressTracking(),
        worksApiService.getPerformanceAnalytics()
      ]);
      
      const reportData = {
        title: 'Executive Summary Report',
        generatedAt: new Date().toISOString(),
        executiveSummary: {
          totalWorks: dashboard.overview.total_works,
          totalValue: dashboard.overview.total_sanctioned,
          utilizationRate: dashboard.overview.utilization_percentage,
          completedWorks: progress.financialProgress.find(p => p.abstract_stage_of_work === WorkStage.COMPLETED)?.work_count || 0,
          delayedWorks: progress.delayedWorks.length,
          averageContractorPerformance: performance.contractorPerformance.reduce((sum, c) => sum + c.performance_score, 0) / performance.contractorPerformance.length
        },
        keyInsights: [
          `${dashboard.overview.total_works} total works under management`,
          `${dashboard.overview.utilization_percentage.toFixed(1)}% budget utilization achieved`,
          `${progress.delayedWorks.length} works require immediate attention`,
          `${dashboard.grantTypeAnalysis.length} different grant types being managed`
        ],
        recommendations: [
          'Focus on delayed works to improve overall completion rate',
          'Optimize resource allocation for better budget utilization',
          'Strengthen monitoring of contractor performance',
          'Implement regular compliance audits'
        ]
      };
      
      downloadReport(reportData, `executive_summary_${new Date().toISOString().split('T')[0]}.json`);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to generate executive report');
    } finally {
      setReportLoading('executive', false);
    }
  };

  // ==========================================
  // REPORT GENERATOR MAPPING
  // ==========================================

  const reportGenerators: Record<string, () => Promise<void>> = {
    progress: generateProgressReport,
    financial: generateFinancialReport,
    delayed: generateDelayedWorksReport,
    district: generateDistrictReport,
    contractor: generateContractorReport,
    monthly: generateMonthlyReport,
    compliance: generateComplianceReport,
    executive: generateExecutiveReport
  };

  // ==========================================
  // FILTER FUNCTIONS
  // ==========================================

  const filteredReports = Object.entries(reportConfigs).filter(([key, config]) => {
    if (selectedCategory === 'all') return true;
    return config.category === selectedCategory;
  });

  const resetFilters = () => {
    setReportFilters({});
  };

  // ==========================================
  // RENDER
  // ==========================================

  return (
    <div className="space-y-8">
      {/* Page Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Works Reports</h1>
          <p className="text-gray-600 mt-1">
            Generate comprehensive reports for analysis and compliance
          </p>
        </div>
        <div className="flex items-center space-x-3">
          <button
            onClick={() => fetchWorks(reportFilters)}
            className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors flex items-center space-x-2"
          >
            <RefreshCw className="w-4 h-4" />
            <span>Refresh Data</span>
          </button>
        </div>
      </div>

      {/* Error Alert */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-center space-x-3">
          <XCircle className="w-5 h-5 text-red-500 flex-shrink-0" />
          <div>
            <h4 className="text-sm font-medium text-red-800">Error generating report</h4>
            <p className="text-sm text-red-600 mt-1">{error}</p>
          </div>
          <button
            onClick={() => setError(null)}
            className="ml-auto text-red-400 hover:text-red-600"
          >
            <XCircle className="w-5 h-5" />
          </button>
        </div>
      )}

      {/* Report Filters Panel */}
      <ReportFiltersPanel
        filters={reportFilters}
        onFiltersChange={setReportFilters}
        onReset={resetFilters}
      />

      {/* Category Filter */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Report Categories</h3>
        <div className="flex flex-wrap gap-2">
          {[
            { key: 'all', label: 'All Reports', count: Object.keys(reportConfigs).length },
            { key: 'progress', label: 'Progress Reports', count: Object.values(reportConfigs).filter(r => r.category === 'progress').length },
            { key: 'financial', label: 'Financial Reports', count: Object.values(reportConfigs).filter(r => r.category === 'financial').length },
            { key: 'operational', label: 'Operational Reports', count: Object.values(reportConfigs).filter(r => r.category === 'operational').length },
            { key: 'compliance', label: 'Compliance Reports', count: Object.values(reportConfigs).filter(r => r.category === 'compliance').length }
          ].map(category => (
            <button
              key={category.key}
              onClick={() => setSelectedCategory(category.key)}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors flex items-center space-x-2 ${
                selectedCategory === category.key
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              <span>{category.label}</span>
              <span className={`px-2 py-0.5 rounded-full text-xs ${
                selectedCategory === category.key
                  ? 'bg-blue-500 text-white'
                  : 'bg-gray-200 text-gray-600'
              }`}>
                {category.count}
              </span>
            </button>
          ))}
        </div>
      </div>

      {/* Reports Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
        {filteredReports.map(([key, config]) => (
          <ReportCard
            key={key}
            {...config}
            onGenerate={reportGenerators[key]}
            loading={loading[key]}
          />
        ))}
      </div>

      {/* Quick Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-blue-100 rounded-lg">
              <Building className="w-6 h-6 text-blue-600" />
            </div>
            <div>
              <p className="text-sm text-gray-600">Total Works</p>
              <p className="text-2xl font-bold text-gray-900">{state.summary?.total_works || 0}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-green-100 rounded-lg">
              <DollarSign className="w-6 h-6 text-green-600" />
            </div>
            <div>
              <p className="text-sm text-gray-600">Total Value</p>
              <p className="text-2xl font-bold text-gray-900">
                {state.summary ? worksApiService.formatCurrency(state.summary.total_estimate_cost) : '₹0'}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-orange-100 rounded-lg">
              <TrendingUp className="w-6 h-6 text-orange-600" />
            </div>
            <div>
              <p className="text-sm text-gray-600">Progress Rate</p>
              <p className="text-2xl font-bold text-gray-900">
                {state.summary?.financial_progress_percentage.toFixed(1) || '0'}%
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-red-100 rounded-lg">
              <Clock className="w-6 h-6 text-red-600" />
            </div>
            <div>
              <p className="text-sm text-gray-600">Reports Generated</p>
              <p className="text-2xl font-bold text-gray-900">
                {Object.values(loading).filter(Boolean).length}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Recent Reports Activity */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-lg font-semibold text-gray-900 flex items-center">
            <FileText className="w-5 h-5 mr-2 text-gray-600" />
            Report Generation Tips
          </h3>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="space-y-4">
            <h4 className="font-medium text-gray-900">Best Practices</h4>
            <ul className="space-y-2 text-sm text-gray-600">
              <li className="flex items-start space-x-2">
                <CheckCircle className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                <span>Apply filters before generating reports for focused analysis</span>
              </li>
              <li className="flex items-start space-x-2">
                <CheckCircle className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                <span>Generate regular progress reports for stakeholder updates</span>
              </li>
              <li className="flex items-start space-x-2">
                <CheckCircle className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                <span>Use delayed works reports to identify bottlenecks</span>
              </li>
              <li className="flex items-start space-x-2">
                <CheckCircle className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                <span>Schedule monthly financial reports for budget tracking</span>
              </li>
            </ul>
          </div>
          
          <div className="space-y-4">
            <h4 className="font-medium text-gray-900">Available Formats</h4>
            <div className="grid grid-cols-2 gap-3">
              <div className="p-3 bg-gray-50 rounded-lg">
                <FileText className="w-6 h-6 text-blue-600 mb-2" />
                <p className="text-sm font-medium text-gray-900">JSON</p>
                <p className="text-xs text-gray-600">Structured data format</p>
              </div>
              <div className="p-3 bg-gray-50 rounded-lg">
                <BarChart3 className="w-6 h-6 text-green-600 mb-2" />
                <p className="text-sm font-medium text-gray-900">CSV</p>
                <p className="text-xs text-gray-600">Spreadsheet compatible</p>
              </div>
            </div>
            <p className="text-sm text-gray-600">
              All reports include metadata, filters applied, and generation timestamp for audit trails.
            </p>
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className="text-center py-8 border-t border-gray-200">
        <p className="text-sm text-gray-500">
          Reports are generated based on current data. For real-time accuracy, refresh data before generating reports.
        </p>
      </div>
    </div>
  );
};

export default WorksReports;